import React from 'react';
import DOMPurify from 'dompurify';
import styles from '../css/HtmlContent.css';

/**
 * TruncatableHtmlContent component
 * 
 * A component that renders HTML content from Strapi with optional truncation,
 * following a similar pattern to TruncatablePortableText
 * 
 * @param {Object} props
 * @param {string} props.html - The HTML content to render
 * @param {string} props.className - Optional className for the container
 * @param {boolean} props.truncate - Whether to truncate the content
 * @param {number} props.maxLength - Maximum number of characters (default: 150)
 * @returns {React.ReactElement}
 */
export const TruncatableHtmlContent = ({ 
  html, 
  className = '',
  truncate = false,
  maxLength = 150
}) => {
  // If no html content, return null
  if (!html) {
    return null;
  }
  
  // Sanitize the HTML to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: ['target'], // Allow target attribute for links
    ADD_TAGS: ['iframe'], // If you want to allow iframes (optional)
    FORBID_TAGS: ['style', 'script'], // Explicitly forbid style and script tags
  });
  
  // If truncation is not requested, render normally
  if (!truncate) {
    return (
      <div className={`${styles.htmlContent} ${className}`} 
           dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    );
  }
  
  // Helper function to truncate HTML content
  const truncateHtml = (htmlContent, maxChars) => {
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Get the text content
    const textContent = tempDiv.textContent || tempDiv.innerText;
    
    // If text is already shorter than maxChars, return as is
    if (textContent.length <= maxChars) {
      return htmlContent;
    }
    
    // Truncate the HTML content
    let currentLength = 0;
    const truncateNode = (node) => {
      if (currentLength >= maxChars) return;
      
      // Handle text nodes (actual content)
      if (node.nodeType === Node.TEXT_NODE) {
        const nodeText = node.textContent;
        const remainingChars = maxChars - currentLength;
        
        if (currentLength + nodeText.length > maxChars) {
          // This text node pushes us over the limit, truncate it
          node.textContent = nodeText.substring(0, remainingChars) + '...';
          currentLength = maxChars;
        } else {
          // This text node fits within our limit
          currentLength += nodeText.length;
        }
      }
      // Handle element nodes (tags like p, div, etc.)
      else if (node.nodeType === Node.ELEMENT_NODE) {
        // Process child nodes one at a time
        const childNodes = Array.from(node.childNodes);
        for (let i = 0; i < childNodes.length; i++) {
          if (currentLength >= maxChars) {
            // We've reached our limit, remove all remaining nodes
            node.removeChild(childNodes[i]);
          } else {
            truncateNode(childNodes[i]);
          }
        }
      }
    };
    
    // Start the truncation process with the root element
    const childNodes = Array.from(tempDiv.childNodes);
    for (let i = 0; i < childNodes.length; i++) {
      if (currentLength >= maxChars) {
        // If we've hit the limit, remove all remaining top-level nodes
        tempDiv.removeChild(childNodes[i]);
      } else {
        truncateNode(childNodes[i]);
      }
    }
    
    return tempDiv.innerHTML;
  };
  
  // Truncate content
  const truncatedContent = truncateHtml(sanitizedHtml, maxLength);
  
  return (
    <div className={`${styles.htmlContent} ${className}`} 
         dangerouslySetInnerHTML={{ __html: truncatedContent }} />
  );
};

export default TruncatableHtmlContent;